import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import {
  SITE_ORIGIN,
  alternateUrls,
  canonicalUrl,
  ogLocale,
  ogLocaleAlternates,
} from '../utils/seo';

const OG_IMAGE = `${SITE_ORIGIN}/images/sparkr-og.jpg`;
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

const SeoHead: React.FC = () => {
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const t = translations[language];

  const pageUrl = canonicalUrl(pathname);
  const alternates = alternateUrls(pathname);

  let title = t.seo.homeTitle;
  let description = t.seo.homeDescription;
  let offer = undefined;

  if (pathname.endsWith('/privacy')) {
    title = t.seo.privacyTitle;
    description = t.seo.privacyDescription;
  } else if (pathname.endsWith('/terms')) {
    title = t.seo.termsTitle;
    description = t.seo.termsDescription;
  } else if (pathname.includes('/offers/')) {
    const slug = pathname.split('/offers/')[1];
    offer = t.offerPages.find((o) => o.slug === slug);
    if (offer) {
      title = offer.seo.title;
      description = offer.seo.description;
    }
  }

  const organizationSchema = {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Sparkr',
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/images/logo.png`,
    email: t.contact.email,
    sameAs: ['https://www.linkedin.com/company/spark-r'],
    description: t.seo.homeDescription,
  };

  const graph: Record<string, unknown>[] = [organizationSchema];

  if (offer) {
    graph.push({
      '@type': 'Service',
      name: offer.title,
      description: offer.seo.description,
      url: pageUrl,
      provider: { '@id': ORGANIZATION_ID },
      areaServed: {
        '@type': 'Place',
        name: 'Worldwide',
      },
      serviceType: offer.subtitle,
    });
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  return (
    <Helmet htmlAttributes={{ lang: language }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />
      <link rel="alternate" hrefLang="fr" href={alternates.fr} />
      <link rel="alternate" hrefLang="en" href={alternates.en} />
      <link rel="alternate" hrefLang="es" href={alternates.es} />
      <link rel="alternate" hrefLang="x-default" href={alternates['x-default']} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="SPARKR.TECH" />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:locale" content={ogLocale(language)} />
      {ogLocaleAlternates(language).map((locale) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:image" content={OG_IMAGE} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SeoHead;
